import React, { useState, useEffect, useRef } from 'react';
import { Plus, Download, Trash2, CheckCircle, Clock, Send, Edit2, Filter, X, RefreshCw, Layers, Loader2, Search, CloudOff, MoreVertical, CheckSquare, Eye, AlertTriangle, Receipt as ReceiptIcon } from 'lucide-react';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { expenseService, tripService } from '../services/api';
import { useNavigate } from 'react-router-dom';
import PreviewModal from '../components/PreviewModal';
import PdfThumbnail from '../components/PdfThumbnail';
import SkeletonLoader from '../components/SkeletonLoader';
import BottomSheet from '../components/mobile/BottomSheet';
import PdfReadySheet from '../components/mobile/PdfReadySheet';
import { useDialog } from '../context/DialogContext';
import useIsMobile from '../hooks/useIsMobile';
import usePendingExpenses from '../hooks/usePendingExpenses';
import { removePending, syncPending } from '../services/offlineQueue';
import { openPdfInNewTab } from '../utils/pdfDelivery';
import { getCurrencyFlag } from '../utils/currencies';
import { EXPENSE_CATEGORIES } from '../utils/categories';

function ExpenseList() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExpenses, setSelectedExpenses] = useState([]);
  const [filter, setFilter] = useState('all'); // all, pending, submitted, reimbursed
  const [clientFilter, setClientFilter] = useState('');
  const [tripFilter, setTripFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [clients, setClients] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewIsPdf, setPreviewIsPdf] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [trips, setTrips] = useState([]);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkFields, setBulkFields] = useState({ client: '', trip_id: '', category: '', status: '' });
  const [bulkApplying, setBulkApplying] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [togglingStatus, setTogglingStatus] = useState(null); // expense id currently toggling
  const searchDebounceRef = useRef(null);
  const navigate = useNavigate();
  const { toast, confirm } = useDialog();
  const isMobile = useIsMobile();
  const pending = usePendingExpenses();
  const [selectionMode, setSelectionMode] = useState(false); // mobile: entered by long-press
  const [actionsFor, setActionsFor] = useState(null); // mobile: expense whose action sheet is open
  const [pdf, setPdf] = useState(null); // mobile: generated PDF waiting to be shared
  const [pendingTripPdf, setPendingTripPdf] = useState(null); // trip id to offer after export
  const pendingCountRef = useRef(pending.length);

  useEffect(() => {
    loadSuggestions();
    loadTrips();
  }, []);

  useEffect(() => {
    loadExpenses();
  }, [filter, clientFilter, tripFilter, dateFrom, dateTo, searchQuery]);

  // Offline receipts were uploaded: refresh the list
  useEffect(() => {
    if (pending.length < pendingCountRef.current) loadExpenses();
    pendingCountRef.current = pending.length;
  }, [pending.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTrips = async () => {
    try {
      const data = await tripService.getTrips();
      setTrips(data);
    } catch (error) {
      console.error('Error loading trips:', error);
    }
  };

  const loadSuggestions = async () => {
    try {
      const suggestions = await expenseService.getFieldSuggestions();
      setClients(suggestions.clients || []);
    } catch (error) {
      console.error('Error loading suggestions:', error);
    }
  };

  const loadExpenses = async () => {
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      if (clientFilter) params.client = clientFilter;
      if (tripFilter) params.trip_id = tripFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (searchQuery) params.search = searchQuery;
      
      const data = await expenseService.getExpenses(params);
      setExpenses(data);
    } catch (error) {
      console.error('Error loading expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setClientFilter('');
    setTripFilter('');
    setDateFrom('');
    setDateTo('');
    setFilter('all');
    setSearchQuery('');
  };

  const hasActiveFilters = clientFilter || tripFilter || dateFrom || dateTo || filter !== 'all' || searchQuery;

  const handleSearchChange = (e) => {
    const value = e.target.value;
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setSearchQuery(value), 300);
  };

  const handleDelete = async (id) => {
    if (!await confirm({
      title: 'Delete this expense?',
      message: 'This action cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true,
    })) {
      return;
    }
    try {
      await expenseService.deleteExpense(id);
      loadExpenses();
    } catch (error) {
      console.error('Error deleting:', error);
      toast('Error deleting expense', 'error');
    }
  };

  const handleDeletePending = async (entry) => {
    if (!await confirm({
      title: 'Discard this offline receipt?',
      message: 'It has not been uploaded yet and will be lost.',
      confirmLabel: 'Discard',
      destructive: true,
    })) return;
    await removePending(entry.id);
  };

  const handleRetrySync = async () => {
    const uploaded = await syncPending();
    if (uploaded > 0) toast(`${uploaded} receipt${uploaded > 1 ? 's' : ''} uploaded`);
    else if (!navigator.onLine) toast("Still offline — we'll retry when you're back online", 'info');
  };

  const STATUS_TRANSITIONS = {
    pending:    { next: 'submitted',  label: 'Submit',          confirm: 'mark as submitted' },
    submitted:  { next: 'reimbursed', label: 'Mark Reimbursed', confirm: 'mark as reimbursed' },
    reimbursed: { next: 'pending',    label: 'Reset to Pending', confirm: 'reset to pending' },
  };

  const handleToggleReimbursed = async (expense) => {
    const transition = STATUS_TRANSITIONS[expense.status] || STATUS_TRANSITIONS.pending;
    setTogglingStatus(expense.id);
    try {
      await expenseService.updateExpense(expense.id, { status: transition.next });
      loadExpenses();
    } catch (error) {
      console.error('Error updating:', error);
      toast('Error updating expense', 'error');
    } finally {
      setTogglingStatus(null);
    }
  };

  const offerTripPdf = async (tripId) => {
    const tripName = trips.find(t => t.id === tripId)?.name || 'this trip';
    if (!await confirm({
      title: 'Travel allowance form',
      message: `All selected expenses belong to "${tripName}".\n\nAlso generate the travel allowance form for this trip?`,
      confirmLabel: 'Generate',
      cancelLabel: 'No thanks',
    })) return;
    try {
      const blob = await tripService.generatePdf(tripId);
      if (isMobile) setPdf({ blob, filename: `Travel_allowance_${tripId}.pdf` });
      else openPdfInNewTab(blob);
    } catch (error) {
      toast('Error generating the travel allowance PDF', 'error');
    }
  };

  const handleExportPdf = async () => {
    if (selectedExpenses.length === 0) {
      toast('Select at least one expense', 'info');
      return;
    }

    if (selectedExpenses.length > 10) {
      if (!await confirm({
        title: `Export ${selectedExpenses.length} expenses?`,
        message: 'This may take a moment.',
        confirmLabel: 'Export',
      })) {
        return;
      }
    }

    setExportingPdf(true);
    try {
      const result = await expenseService.exportToPdf(selectedExpenses);
      if (isMobile) {
        // Mobile: fetch the file, then share/save it from a fresh tap (iOS
        // blocks window.open and navigator.share after an await)
        const blob = await expenseService.fetchFileBlob(result.path);
        setPdf({ blob: new Blob([blob], { type: 'application/pdf' }), filename: result.path.split('/').pop() || 'expenses.pdf' });
        if (result.trip_id) setPendingTripPdf(result.trip_id);
      } else {
        window.open(expenseService.getFileUrl(result.path), '_blank');
        if (result.trip_id) await offerTripPdf(result.trip_id);
      }
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast('Error exporting PDF', 'error');
    } finally {
      setExportingPdf(false);
    }
  };

  const closePdfSheet = () => {
    setPdf(null);
    if (pendingTripPdf) {
      const tripId = pendingTripPdf;
      setPendingTripPdf(null);
      offerTripPdf(tripId);
    }
  };

  const toggleExpenseSelection = (id) => {
    setSelectedExpenses(prev => 
      prev.includes(id) ? prev.filter(expId => expId !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    setSelectedExpenses(expenses.map(exp => exp.id));
  };

  const deselectAll = () => {
    setSelectedExpenses([]);
    setBulkEditOpen(false);
    setSelectionMode(false);
  };

  const enterSelectionMode = (id) => {
    if (navigator.vibrate) navigator.vibrate(15);
    setSelectionMode(true);
    setSelectedExpenses(prev => (prev.includes(id) ? prev : [...prev, id]));
  };

  // Leaving selection mode when the last item is deselected
  useEffect(() => {
    if (selectionMode && selectedExpenses.length === 0) setSelectionMode(false);
  }, [selectionMode, selectedExpenses.length]);

  const handleBulkEdit = async () => {
    setBulkApplying(true);
    try {
      // Build payload — only include fields the user touched
      const fields = {};
      if (bulkFields.client !== '') fields.client = bulkFields.client;
      if (bulkFields.category !== '') fields.category = bulkFields.category;
      if (bulkFields.status !== '') fields.status = bulkFields.status;
      // trip_id: '' = no change, 'none' = clear, numeric string = assign
      if (bulkFields.trip_id === 'none') fields.trip_id = null;
      else if (bulkFields.trip_id !== '') fields.trip_id = parseInt(bulkFields.trip_id, 10);

      if (Object.keys(fields).length === 0) {
        toast('No fields selected to update', 'info');
        return;
      }

      const result = await expenseService.bulkUpdateExpenses(selectedExpenses, fields);
      setBulkEditOpen(false);
      setBulkFields({ client: '', trip_id: '', category: '', status: '' });
      setSelectedExpenses([]);
      setSelectionMode(false);
      loadExpenses();
      toast(`Updated ${result.updated} expense${result.updated !== 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Bulk update error:', error);
      toast('Error applying bulk update', 'error');
    } finally {
      setBulkApplying(false);
    }
  };

  const handleOpenPreview = (expense) => {
    const isPdf = expense.image_path.toLowerCase().endsWith('.pdf');
    const fileUrl = expenseService.getFileUrl(expense.image_url || expense.thumbnail_url);
    setPreviewFile(expense);
    setPreviewUrl(fileUrl);
    setPreviewIsPdf(isPdf);
    setPreviewOpen(true);
  };

  const handleRegenerateThumbnails = async () => {
    setRegenerating(true);
    try {
      const result = await expenseService.regenerateThumbnails(true); // force=true to regenerate all
      toast(`Thumbnails: ${result.regenerated} generated, ${result.skipped} skipped, ${result.errors} errors`, 'info');
      loadExpenses(); // Refresh to show new thumbnails
    } catch (error) {
      console.error('Error regenerating thumbnails:', error);
      toast('Error regenerating thumbnails', 'error');
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="hidden md:flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">My expenses</h1>
          <button
            disabled
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 opacity-50 cursor-not-allowed"
          >
            <Plus className="w-5 h-5 mr-2" />
            New expense
          </button>
        </div>
        
        {/* Skeleton grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonLoader key={i} variant="card" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-4 md:space-y-6 ${selectionMode ? 'pb-20 md:pb-0' : ''}`}>
      <div className="hidden md:flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">My expenses</h1>
        <button
          onClick={() => navigate('/expenses/new')}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          aria-label="Add new expense"
        >
          <Plus className="w-5 h-5 mr-2" />
          <span className="hidden sm:inline">New expense</span>
          <span className="sm:hidden">New</span>
        </button>
      </div>

      {/* Receipts captured offline, waiting to upload */}
      {pending.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl md:rounded-lg p-4">
          <div className="flex items-center justify-between gap-3 mb-3">
            <p className="text-sm font-semibold text-amber-900 flex items-center gap-2">
              <CloudOff className="w-4 h-4" />
              {pending.length} receipt{pending.length > 1 ? 's' : ''} waiting to upload
            </p>
            <button
              type="button"
              onClick={handleRetrySync}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white border border-amber-300 text-sm text-amber-800 active:bg-amber-100"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
          <div className="space-y-2">
            {pending.map(entry => (
              <PendingRow key={entry.id} entry={entry} onDiscard={() => handleDeletePending(entry)} />
            ))}
          </div>
        </div>
      )}

      {/* Filtres et actions */}
      <div className="bg-white shadow rounded-2xl md:rounded-lg p-3 md:p-4 space-y-3 md:space-y-4">
        {/* Search bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search by merchant, description, attendees…"
            defaultValue={searchQuery}
            onChange={handleSearchChange}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <div className="flex gap-2 items-center min-w-max">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  filter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilter('pending')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  filter === 'pending'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Pending
              </button>
              <button
                onClick={() => setFilter('submitted')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  filter === 'submitted'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Submitted
              </button>
              <button
                onClick={() => setFilter('reimbursed')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  filter === 'reimbursed'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Reimbursed
              </button>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`ml-2 px-3 py-2 rounded-md text-sm font-medium flex items-center gap-1 ${
                  showFilters || hasActiveFilters
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Filter className="w-4 h-4" />
                Filters
                {hasActiveFilters && <span className="ml-1 w-2 h-2 bg-blue-600 rounded-full"></span>}
              </button>
            </div>
          </div>

          {expenses.length > 0 && (
            <div className="hidden md:flex gap-2">
              <button
                onClick={selectAll}
                className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700"
              >
                Select all
              </button>
              <button
                onClick={deselectAll}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-700"
              >
                Deselect all
              </button>
              {selectedExpenses.length > 0 && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setBulkEditOpen(prev => !prev)}
                    className={`inline-flex items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium ${
                      bulkEditOpen
                        ? 'border-purple-500 text-purple-700 bg-purple-100'
                        : 'border-purple-300 text-purple-700 bg-purple-50 hover:bg-purple-100'
                    }`}
                  >
                    <Layers className="w-4 h-4 mr-2" />
                    Bulk Edit ({selectedExpenses.length})
                  </button>
                  <button
                    onClick={handleExportPdf}
                    disabled={exportingPdf}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {exportingPdf ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    {exportingPdf ? 'Exporting…' : `Export PDF (${selectedExpenses.length})`}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Advanced filter panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Project/Client</label>
                <select
                  value={clientFilter}
                  onChange={(e) => setClientFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All projects</option>
                  {clients.map((client) => (
                    <option key={client} value={client}>{client}</option>
                  ))}
                </select>
              </div>

              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Trip</label>
                <select
                  value={tripFilter}
                  onChange={(e) => setTripFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All trips</option>
                  {trips.map((trip) => (
                    <option key={trip.id} value={trip.id}>{trip.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex-1 min-w-[150px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Start date</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="flex-1 min-w-[150px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">End date</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="px-3 py-2 text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                >
                  <X className="w-4 h-4" />
                  Clear
                </button>
              )}
              
              <button
                onClick={handleRegenerateThumbnails}
                disabled={regenerating}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-700 flex items-center gap-1 ml-auto disabled:opacity-50"
                title="Generate missing PDF thumbnails"
              >
                <RefreshCw className={`w-4 h-4 ${regenerating ? 'animate-spin' : ''}`} />
                {regenerating ? 'Generating...' : 'Regenerate thumbnails'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bulk edit panel (desktop) */}
      {bulkEditOpen && selectedExpenses.length > 0 && !isMobile && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-purple-800">
              Bulk edit — {selectedExpenses.length} expense{selectedExpenses.length !== 1 ? 's' : ''} selected
            </h3>
            <p className="text-xs text-purple-600">Leave a field empty to keep existing values unchanged.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Trip</label>
              <select
                value={bulkFields.trip_id}
                onChange={(e) => setBulkFields(prev => ({ ...prev, trip_id: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="">— no change —</option>
                <option value="none">Remove trip</option>
                {trips.map(trip => (
                  <option key={trip.id} value={trip.id}>{trip.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project / Client</label>
              <input
                type="text"
                list="bulk-clients-list"
                value={bulkFields.client}
                onChange={(e) => setBulkFields(prev => ({ ...prev, client: e.target.value }))}
                placeholder="— no change —"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-purple-500 focus:border-purple-500"
              />
              <datalist id="bulk-clients-list">
                {clients.map(c => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={bulkFields.category}
                onChange={(e) => setBulkFields(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="">— no change —</option>
                {EXPENSE_CATEGORIES.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={bulkFields.status}
                onChange={(e) => setBulkFields(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="">— no change —</option>
                <option value="pending">Pending</option>
                <option value="submitted">Submitted</option>
                <option value="reimbursed">Reimbursed</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setBulkEditOpen(false)}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={handleBulkEdit}
              disabled={bulkApplying}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
            >
              {bulkApplying ? 'Applying...' : `Apply to ${selectedExpenses.length} expense${selectedExpenses.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      )}

      {/* Expense list */}
      {expenses.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">No expenses found</p>
          <button
            onClick={() => navigate('/expenses/new')}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add my first expense
          </button>
        </div>
      ) : isMobile ? (
        <div className="bg-white rounded-2xl shadow divide-y divide-gray-100 overflow-hidden">
          {selectionMode === false && (
            <p className="px-4 py-2 text-xs text-gray-400">Tip: press and hold an expense to select several</p>
          )}
          {expenses.map((expense) => (
            <MobileExpenseRow
              key={expense.id}
              expense={expense}
              selectionMode={selectionMode}
              isSelected={selectedExpenses.includes(expense.id)}
              onToggleSelect={() => toggleExpenseSelection(expense.id)}
              onLongPress={() => enterSelectionMode(expense.id)}
              onOpen={() => navigate(`/expenses/${expense.id}/edit`)}
              onMore={() => setActionsFor(expense)}
              onPreview={handleOpenPreview}
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {expenses.map((expense) => (
            <ExpenseCard
              key={expense.id}
              expense={expense}
              isSelected={selectedExpenses.includes(expense.id)}
              isToggling={togglingStatus === expense.id}
              onToggleSelect={() => toggleExpenseSelection(expense.id)}
              onDelete={() => handleDelete(expense.id)}
              onToggleReimbursed={() => handleToggleReimbursed(expense)}
              onEdit={() => navigate(`/expenses/${expense.id}/edit`)}
              onPreview={handleOpenPreview}
            />
          ))}
        </div>
      )}

      <PreviewModal
        file={previewFile}
        fileUrl={previewUrl}
        isPdf={previewIsPdf}
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />

      {/* Mobile: sticky selection action bar */}
      {isMobile && selectionMode && (
        <div className="fixed inset-x-0 bottom-above-nav z-30 px-3">
          <div className="flex items-center gap-1 rounded-2xl bg-gray-900 text-white shadow-xl px-2 py-2">
            <button type="button" onClick={deselectAll} className="p-2 rounded-xl active:bg-gray-700" aria-label="Cancel selection">
              <X className="w-5 h-5" />
            </button>
            <span className="flex-1 text-sm font-medium">{selectedExpenses.length} selected</span>
            <button
              type="button"
              onClick={selectedExpenses.length === expenses.length ? () => setSelectedExpenses([]) : selectAll}
              className="p-2 rounded-xl active:bg-gray-700"
              aria-label="Select all"
            >
              <CheckSquare className="w-5 h-5" />
            </button>
            <button type="button" onClick={() => setBulkEditOpen(true)} className="inline-flex items-center gap-1 px-3 py-2 rounded-xl active:bg-gray-700 text-sm">
              <Layers className="w-4 h-4" />
              Edit
            </button>
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={exportingPdf}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-green-600 active:bg-green-700 text-sm font-medium disabled:opacity-60"
            >
              {exportingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Export
            </button>
          </div>
        </div>
      )}

      {/* Mobile: bulk edit sheet */}
      <BottomSheet
        open={isMobile && bulkEditOpen && selectedExpenses.length > 0}
        onClose={() => setBulkEditOpen(false)}
        title={`Edit ${selectedExpenses.length} expense${selectedExpenses.length !== 1 ? 's' : ''}`}
        footer={
          <button
            onClick={handleBulkEdit}
            disabled={bulkApplying}
            className="w-full py-3 rounded-xl bg-purple-600 text-white font-semibold disabled:opacity-50"
          >
            {bulkApplying ? 'Applying…' : 'Apply'}
          </button>
        }
      >
        <p className="text-sm text-gray-500 mb-3">Leave a field empty to keep existing values.</p>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Trip</label>
            <select
              value={bulkFields.trip_id}
              onChange={(e) => setBulkFields(prev => ({ ...prev, trip_id: e.target.value }))}
              className="w-full px-3 py-3 border border-gray-300 rounded-xl text-base"
            >
              <option value="">— no change —</option>
              <option value="none">Remove trip</option>
              {trips.map(trip => (
                <option key={trip.id} value={trip.id}>{trip.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project / Client</label>
            <input
              type="text"
              list="bulk-clients-list-mobile"
              value={bulkFields.client}
              onChange={(e) => setBulkFields(prev => ({ ...prev, client: e.target.value }))}
              placeholder="— no change —"
              className="w-full px-3 py-3 border border-gray-300 rounded-xl text-base"
            />
            <datalist id="bulk-clients-list-mobile">
              {clients.map(c => <option key={c} value={c} />)}
            </datalist>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={bulkFields.category}
              onChange={(e) => setBulkFields(prev => ({ ...prev, category: e.target.value }))}
              className="w-full px-3 py-3 border border-gray-300 rounded-xl text-base"
            >
              <option value="">— no change —</option>
              {EXPENSE_CATEGORIES.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={bulkFields.status}
              onChange={(e) => setBulkFields(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-3 border border-gray-300 rounded-xl text-base"
            >
              <option value="">— no change —</option>
              <option value="pending">Pending</option>
              <option value="submitted">Submitted</option>
              <option value="reimbursed">Reimbursed</option>
            </select>
          </div>
        </div>
      </BottomSheet>

      {/* Mobile: per-expense actions */}
      <BottomSheet
        open={Boolean(actionsFor)}
        onClose={() => setActionsFor(null)}
        title={actionsFor ? `${actionsFor.amount.toFixed(2)} ${actionsFor.currency}${actionsFor.merchant ? ` · ${actionsFor.merchant}` : ''}` : ''}
      >
        {actionsFor && (() => {
          const cfg = CARD_STATUS_CONFIG[actionsFor.status] || CARD_STATUS_CONFIG.pending;
          const run = (fn) => () => { setActionsFor(null); fn(); };
          return (
            <div className="space-y-1">
              <SheetRow icon={Eye} label="View receipt" onClick={run(() => handleOpenPreview(actionsFor))} />
              <SheetRow icon={Edit2} label="Edit" onClick={run(() => navigate(`/expenses/${actionsFor.id}/edit`))} />
              <SheetRow icon={cfg.Icon} label={cfg.next} onClick={run(() => handleToggleReimbursed(actionsFor))} />
              <SheetRow icon={CheckSquare} label="Select" onClick={run(() => enterSelectionMode(actionsFor.id))} />
              <SheetRow icon={Trash2} label="Delete" destructive onClick={run(() => handleDelete(actionsFor.id))} />
            </div>
          );
        })()}
      </BottomSheet>

      <PdfReadySheet pdf={pdf} onClose={closePdfSheet} />
    </div>
  );
}

function SheetRow({ icon: Icon, label, onClick, destructive = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-1 py-3 rounded-xl text-left active:bg-gray-100 ${destructive ? 'text-red-600' : 'text-gray-900'}`}
    >
      <Icon className={`w-5 h-5 ${destructive ? '' : 'text-gray-500'}`} />
      <span className="font-medium">{label}</span>
    </button>
  );
}

const STATUS_BADGE = {
  pending: 'bg-yellow-100 text-yellow-800',
  submitted: 'bg-blue-100 text-blue-800',
  reimbursed: 'bg-green-100 text-green-800',
};

const LONG_PRESS_MS = 450;

/** Compact list row for mobile. Long-press enters selection mode. */
function MobileExpenseRow({ expense, selectionMode, isSelected, onToggleSelect, onLongPress, onOpen, onMore, onPreview }) {
  const timerRef = useRef(null);
  const longPressedRef = useRef(false);
  const startRef = useRef(null);
  const isPdf = expense.image_path.toLowerCase().endsWith('.pdf');
  const thumbUrl = expenseService.getFileUrl(expense.thumbnail_url);

  const clear = () => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  const onPointerDown = (e) => {
    longPressedRef.current = false;
    startRef.current = { x: e.clientX, y: e.clientY };
    timerRef.current = setTimeout(() => {
      longPressedRef.current = true;
      onLongPress();
    }, LONG_PRESS_MS);
  };

  // Cancel the long-press when the finger moves (the user is scrolling)
  const onPointerMove = (e) => {
    if (!timerRef.current || !startRef.current) return;
    if (Math.abs(e.clientX - startRef.current.x) > 8 || Math.abs(e.clientY - startRef.current.y) > 8) clear();
  };

  const onClick = () => {
    if (longPressedRef.current) {
      longPressedRef.current = false;
      return;
    }
    if (selectionMode) onToggleSelect();
    else onOpen();
  };

  return (
    <div
      className={`flex items-center gap-3 px-3 py-3 select-none ${isSelected ? 'bg-blue-50' : 'active:bg-gray-50'}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={clear}
      onPointerCancel={clear}
      onPointerLeave={clear}
      onContextMenu={(e) => e.preventDefault()}
      onClick={onClick}
      role="button"
      aria-pressed={selectionMode ? isSelected : undefined}
    >
      {selectionMode && (
        <span className={`w-6 h-6 flex-shrink-0 rounded-full border-2 flex items-center justify-center ${
          isSelected ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300'
        }`}>
          {isSelected && <CheckCircle className="w-4 h-4" />}
        </span>
      )}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); if (selectionMode) onToggleSelect(); else onPreview(expense); }}
        className="w-14 h-14 flex-shrink-0 rounded-lg bg-gray-100 overflow-hidden"
        aria-label="View receipt"
      >
        {thumbUrl ? (
          <img src={thumbUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : isPdf ? (
          <PdfThumbnail url={expenseService.getFileUrl(expense.image_url)} className="w-full h-full" />
        ) : (
          <span className="w-full h-full flex items-center justify-center text-gray-300">
            <ReceiptIcon className="w-6 h-6" />
          </span>
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <p className="font-semibold text-gray-900 truncate">{expense.merchant || expense.category || 'Expense'}</p>
          <p className="font-semibold text-gray-900 whitespace-nowrap">
            {expense.amount.toFixed(2)} <span className="text-xs font-medium text-gray-500">{expense.currency}</span>
          </p>
        </div>
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <p className="text-sm text-gray-500 truncate">
            {format(new Date(expense.date), 'd MMM yyyy', { locale: enUS })}
            {expense.currency !== 'EUR' && expense.amount_in_eur ? ` · ≈ ${expense.amount_in_eur.toFixed(2)} €` : ''}
          </p>
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium capitalize ${STATUS_BADGE[expense.status] || STATUS_BADGE.pending}`}>
            {expense.status}
          </span>
        </div>
      </div>
      {!selectionMode && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onMore(); }}
          className="-mr-1 p-2 rounded-full text-gray-400 active:bg-gray-100"
          aria-label="More actions"
        >
          <MoreVertical className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}

/** A receipt captured offline and not uploaded yet. */
function PendingRow({ entry, onDiscard }) {
  const [url, setUrl] = useState(null);
  const isImage = entry.file.type.startsWith('image/');

  useEffect(() => {
    if (!isImage) return undefined;
    const objectUrl = URL.createObjectURL(entry.file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [entry.file, isImage]);

  const amount = parseFloat(entry.data.amount);

  return (
    <div className="flex items-center gap-3 bg-white rounded-xl p-2">
      <div className="w-12 h-12 flex-shrink-0 rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center">
        {url ? <img src={url} alt="" className="w-full h-full object-cover" /> : <CloudOff className="w-5 h-5 text-gray-400" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-900 truncate">
          {isNaN(amount) ? '—' : amount.toFixed(2)} {entry.data.currency}
          {entry.data.merchant ? <span className="font-normal text-gray-500"> · {entry.data.merchant}</span> : null}
        </p>
        {entry.error ? (
          <p className="text-xs text-red-600 flex items-center gap-1 truncate">
            <AlertTriangle className="w-3 h-3 flex-shrink-0" />
            {entry.error}
          </p>
        ) : (
          <p className="text-xs text-amber-700">Pending sync · {entry.data.date}</p>
        )}
      </div>
      <button
        type="button"
        onClick={onDiscard}
        className="p-2 rounded-full text-gray-400 active:bg-gray-100"
        aria-label="Discard offline receipt"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

const CARD_STATUS_CONFIG = {
  pending:    { next: 'Submit',           btnClass: 'border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100',    Icon: Send },
  submitted:  { next: 'Mark Reimbursed',  btnClass: 'border-green-300 text-green-700 bg-green-50 hover:bg-green-100', Icon: CheckCircle },
  reimbursed: { next: 'Reset to Pending', btnClass: 'border-yellow-300 text-yellow-700 bg-yellow-50 hover:bg-yellow-100', Icon: Clock },
};

function ExpenseCard({ expense, isSelected, isToggling, onToggleSelect, onDelete, onToggleReimbursed, onEdit, onPreview }) {
  const isPdf = expense.image_path.toLowerCase().endsWith('.pdf');
  const thumbnailUrl = expenseService.getFileUrl(expense.thumbnail_url);
  const imageUrl = expenseService.getFileUrl(expense.thumbnail_url || expense.image_url);

  return (
    <div className={`bg-white rounded-lg shadow overflow-hidden ${isSelected ? 'ring-2 ring-blue-500' : ''}`}>
      {/* Image/PDF Thumbnail */}
      {imageUrl && (
        <div 
          className="h-48 bg-gray-100 relative cursor-pointer hover:opacity-90 transition-opacity"
          onClick={() => onPreview(expense)}
        >
          {isPdf && !thumbnailUrl ? (
            // Fallback to client-side PDF rendering if no server thumbnail
            <PdfThumbnail
              url={expenseService.getFileUrl(expense.image_url)}
              className="w-full h-full"
            />
          ) : (
            // Use server-generated thumbnail (works for both images and PDFs)
            <img
              src={imageUrl}
              alt="Receipt"
              className="w-full h-full object-cover"
            />
          )}
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            onClick={(e) => e.stopPropagation()}
            className="absolute top-3 left-3 w-5 h-5 rounded border-gray-300 cursor-pointer"
            aria-label="Select expense"
          />
        </div>
      )}

      {/* Content */}
      <div className="p-4">
        <div className="flex justify-between items-start mb-2">
          <div>
            <p className="text-sm text-gray-500">
              {format(new Date(expense.date), 'MMMM dd, yyyy', { locale: enUS })}
            </p>
            <p className="text-2xl font-bold text-gray-900 mb-1">
              {getCurrencyFlag(expense.currency)} {expense.amount.toFixed(2)} {expense.currency}
            </p>
            {/* EUR Conversion Display */}
            {expense.currency !== 'EUR' && expense.amount_in_eur && (
              <div className="space-y-0.5">
                <p className="text-lg font-semibold text-blue-700">
                  💶 {expense.amount_in_eur.toFixed(2)} EUR
                </p>
                {expense.conversion_rate && (
                  <p className="text-xs text-gray-500">
                    1 {expense.currency} = {expense.conversion_rate.toFixed(4)} EUR
                  </p>
                )}
              </div>
            )}
          </div>
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              expense.status === 'reimbursed'
                ? 'bg-green-100 text-green-800'
                : expense.status === 'submitted'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}
          >
            {expense.status === 'reimbursed' ? (
              <>
                <CheckCircle className="w-3 h-3 mr-1" />
                Reimbursed
              </>
            ) : expense.status === 'submitted' ? (
              <>
                <Send className="w-3 h-3 mr-1" />
                Submitted
              </>
            ) : (
              <>
                <Clock className="w-3 h-3 mr-1" />
                Pending
              </>
            )}
          </span>
        </div>

        {expense.merchant && (
          <p className="text-sm font-medium text-gray-900 mb-1">{expense.merchant}</p>
        )}

        {expense.category && (
          <p className="text-sm text-gray-500 mb-2">{expense.category}</p>
        )}

        {expense.description && (
          <p className="text-sm text-gray-600 line-clamp-2 mb-3">{expense.description}</p>
        )}

        {expense.attendees && (
          <p className="text-xs text-gray-500 mb-3">
            With: {expense.attendees}
          </p>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-3 border-t border-gray-200">
          <button
            onClick={onEdit}
            className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            aria-label="Edit expense"
            title="Edit this expense"
          >
            <Edit2 className="w-4 h-4 mr-1" />
            Edit
          </button>
          {(() => {
            const cfg = CARD_STATUS_CONFIG[expense.status] || CARD_STATUS_CONFIG.pending;
            return (
              <button
                onClick={onToggleReimbursed}
                disabled={isToggling}
                className={`flex-1 inline-flex items-center justify-center px-3 py-2 border rounded-md text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed ${cfg.btnClass}`}
                aria-label={cfg.next}
                title={cfg.next}
              >
                {isToggling
                  ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  : <cfg.Icon className="w-4 h-4 mr-1" />
                }
                {cfg.next}
              </button>
            );
          })()}
          <button
            onClick={onDelete}
            className="inline-flex items-center justify-center px-3 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100"
            aria-label="Delete expense"
            title="Delete this expense"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default ExpenseList;
